import { cn } from '@ui-kit/utils';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { CreditTierSE } from '@/api/credit/types';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { formatInterestRate } from '@/localization/percentages';
import { device } from '@/ui-kit/breakpoints';
import { formatDate, parseDate } from '@/utils/date';

type Payload = {
  installments: CreditTierSE['pmt'];
  amount: number;
  totalInstallmentFees: number;
  totalCost: number;
  totalAmount: number;
  interest: number;
  effectiveInterest: number;
};
type Props = ModalProps<Payload>;

const TableWrapper = styled.div`
  overflow: auto;
  margin-inline: calc(-1 * var(--modal-horizontal-padding));
  padding-inline: var(--modal-horizontal-padding);

  @media (${device.tablet}) {
    margin-inline: unset;
    padding-inline: unset;
  }
`;

const ModalTable = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
`;

const Th = ({ className, ...props }: ComponentProps<'th'>) => (
  <th className={cn('border-border-default border-b px-1 py-3 text-right', className)} {...props} />
);

const Td = ({ className, ...props }: ComponentProps<'td'>) => (
  <td className={cn('border-border-default border-b px-1 py-3 text-right', className)} {...props} />
);

const PaybackPlan = ({
  payload: {
    installments,
    amount,
    totalCost,
    totalAmount,
    interest,
    effectiveInterest,
    totalInstallmentFees,
  },
}: Props) => {
  const { t } = useTranslation(['Modal', 'Common']);

  return (
    <Modal title={t('PaybackPlanModal.title')}>
      <div className="flex flex-col gap-2">
        <p>{t('PaybackPlanModal.messageBelowTitle1')}</p>
        <p>{t('PaybackPlanModal.messageBelowTitle2')}</p>
        <TableWrapper>
          <ModalTable>
            <thead>
              <tr>
                <Th>{t('PaybackPlanModal.dueDate')}</Th>
                <Th>{t('PaybackPlanModal.interest')}</Th>
                <Th>{t('PaybackPlanModal.amortization')}</Th>
                <Th>{t('PaybackPlanModal.installmentFee')}</Th>
                <Th>{t('PaybackPlanModal.totalToPay')}</Th>
              </tr>
            </thead>
            <tbody>
              {installments.map((item, index) => (
                <tr key={index}>
                  <Td className="text-left">{formatDate(parseDate(item.dueDate))}</Td>
                  <Td>{t('Common:moneyWithTrailingZeros', { amount: item.interest })}</Td>
                  <Td>{t('Common:moneyWithTrailingZeros', { amount: item.principal })}</Td>
                  <Td>{t('Common:moneyWithTrailingZeros', { amount: item.installmentFee })}</Td>
                  <Td>{t('Common:moneyWithTrailingZeros', { amount: item.total })}</Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="[&>td]:text-body-semibold [&>td]:border-none [&>td]:py-4">
                <Td className="text-left">{t('PaybackPlanModal.total')}</Td>
                <Td>{t('Common:moneyWithTrailingZeros', { amount: totalCost })}</Td>
                <Td>{t('Common:moneyWithTrailingZeros', { amount })}</Td>
                <Td>{t('Common:moneyWithTrailingZeros', { amount: totalInstallmentFees })}</Td>
                <Td>{t('Common:moneyWithTrailingZeros', { amount: totalAmount })}</Td>
              </tr>
            </tfoot>
          </ModalTable>
        </TableWrapper>

        <p>
          {t('PaybackPlanModal.messageBelowTable', {
            effectiveInterest: formatInterestRate(effectiveInterest),
            interest: formatInterestRate(interest),
            totalAmount,
            amount,
            totalCost,
          })}
        </p>
      </div>
    </Modal>
  );
};

export default PaybackPlan;
